import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import {
  getNextUpdateDelay,
  timeAgoString,
  timeRemainingString,
  type TimeAgoVariant,
} from "./utils";

interface UseRelativeTimeOptions {
  futureDate?: boolean;
  variant?: TimeAgoVariant;
}

export function useRelativeTime(
  timestamp: string,
  { futureDate = false, variant = "compact" }: UseRelativeTimeOptions = {},
) {
  const intl = useIntl();
  const [now, setNow] = useState(() => Date.now());
  const isFirstRun = useRef(true);

  // Snap back to the current time whenever the timestamp itself changes, so we don't
  // compute the first render off a `now` left over from a since-cleared long delay.
  // Skipped on mount, where `now` is already fresh and setting it would only cost an
  // extra render plus a discarded timer.
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setNow(Date.now());
  }, [timestamp]);

  useEffect(() => {
    const delay = getNextUpdateDelay(timestamp, now, variant);
    const timer = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(timer);
  }, [timestamp, now, variant]);

  const date = new Date(timestamp);

  return futureDate
    ? timeRemainingString(intl, date, now)
    : timeAgoString(intl, date, now, variant);
}
